<template>
  <div class="operation-report-container pa-0">
    <va-card class="main-card" v-if="crimeData">
      <div class="header-section d-flex justify-content-between align-items-center p-4 bg-light border-bottom">
        <div>
          <h4 class="mb-1 poppinsbold text-primary">Operation Report</h4>
          <p class="text-muted small mb-0">
             <b-badge variant="primary" class="mr-2" v-if="crimeData.crimeReportID">{{ crimeData.crimeReportID }}</b-badge>
             Record and manage field operation details, seizures, and accused information.
          </p>
        </div>
        <div v-if="crimeData.isOperationSubmitted || crimeData.isOperationApproved">
          <b-button variant="primary" @click="downloadPDF" class="shadow-sm">
            <i class="fa fa-file-pdf-o mr-2"></i> Download Operation Report
          </b-button>
        </div>
      </div>

      <div class="content-section p-4">
        <div v-if="isEditing || (!isAdmin && !crimeData.isOperationSubmitted)">
          <!-- Analyst Form -->
          <b-form @submit.prevent>

            <!-- Basic Details Section -->
            <div class="form-section mb-5">
              <div class="section-title mb-4 d-flex align-items-center">
                <div class="icon-box mr-3"><i class="fa fa-info-circle"></i></div>
                <h5 class="mb-0 poppinsbold">Basic Operation Details</h5>
              </div>
              <b-row>
                <b-col md="4">
                  <b-form-group label="Date & Time" label-class="field-label">
                    <b-form-input type="datetime-local" v-model="operationData.dateTime" required class="custom-input"></b-form-input>
                  </b-form-group>
                </b-col>
                <b-col md="4">
                   <b-form-group label="Country" label-class="field-label">
                    <b-form-input v-model="operationData.location.country" required class="custom-input"></b-form-input>
                  </b-form-group>
                </b-col>
                <b-col md="4">
                   <b-form-group label="State" label-class="field-label">
                    <b-form-input v-model="operationData.location.state" required class="custom-input"></b-form-input>
                  </b-form-group>
                </b-col>
                <b-col md="4">
                   <b-form-group label="District" label-class="field-label">
                    <b-form-input v-model="operationData.location.district" required class="custom-input"></b-form-input>
                  </b-form-group>
                </b-col>
                <b-col md="4">
                  <b-form-group label="Forest Range" label-class="field-label">
                    <b-form-input v-model="operationData.location.forestRange" required placeholder="e.g. Nagarhole South" class="custom-input"></b-form-input>
                  </b-form-group>
                </b-col>
                <b-col md="4">
                  <b-form-group label="Exact Location" label-class="field-label">
                    <b-form-input v-model="operationData.location.exactLocation" required placeholder="Coordinates or Landmark" class="custom-input"></b-form-input>
                  </b-form-group>
                </b-col>
              </b-row>
              <b-row class="mt-3">
                <b-col md="6">
                  <b-form-group label="Agencies Involved" label-class="field-label">
                    <b-form-textarea v-model="operationData.agenciesInvolved" placeholder="e.g. WCCB, State Police, Forest Dept" rows="2" class="custom-input"></b-form-textarea>
                  </b-form-group>
                </b-col>
                <b-col md="6">
                  <b-form-group label="Modus Operandi" label-class="field-label">
                    <b-form-textarea v-model="operationData.modusOperandi" placeholder="Describe how the crime was committed..." rows="2" class="custom-input"></b-form-textarea>
                  </b-form-group>
                </b-col>
              </b-row>
            </div>

            <!-- Seizures Section -->
            <div class="form-section mb-5">
              <div class="section-title mb-4 d-flex align-items-center justify-content-between">
                <div class="d-flex align-items-center">
                  <div class="icon-box mr-3 bg-success"><i class="fa fa-leaf"></i></div>
                  <h5 class="mb-0 poppinsbold">Seizures: Live Wildlife</h5>
                </div>
                <b-button variant="outline-success" size="sm" @click="addItem('wildlifeLive')" class="rounded-pill px-3">
                  <i class="fa fa-plus mr-1"></i> Add Entry
                </b-button>
              </div>

              <div v-if="!operationData.seizures.wildlifeLive || operationData.seizures.wildlifeLive.length === 0" class="empty-state text-center p-4 border rounded mb-4">
                <p class="text-muted mb-0 small">No live wildlife seizures recorded. Click "Add Entry" to begin.</p>
              </div>

              <div v-for="(item, index) in operationData.seizures.wildlifeLive" :key="'live-' + index" class="entry-card mb-4">
                <div class="entry-header d-flex justify-content-between align-items-center px-3 py-2">
                  <span class="small font-weight-bold text-uppercase">Entry #{{ index + 1 }}</span>
                  <b-button variant="link" class="text-danger p-0 small" @click="removeItem('wildlifeLive', index)">
                    <i class="fa fa-trash mr-1"></i> Remove
                  </b-button>
                </div>
                <div class="p-3">
                  <b-row>
                    <b-col md="6">
                      <b-form-group label="Species" label-class="field-label-small">
                        <b-form-select v-model="item.species" required class="custom-select">
                          <option :value="null">Select Species</option>
                          <option v-for="s in speciesOptions" :key="s._id" :value="s._id">{{ s.commonName }}</option>
                        </b-form-select>
                      </b-form-group>
                    </b-col>
                    <b-col md="3">
                      <b-form-group label="Count" label-class="field-label-small">
                        <b-form-input type="number" v-model="item.count" min="1" class="custom-input"></b-form-input>
                      </b-form-group>
                    </b-col>
                    <b-col md="3">
                      <b-form-group label="Status" label-class="field-label-small">
                        <b-form-select v-model="item.status" :options="['Alive', 'Dead']" class="custom-select"></b-form-select>
                      </b-form-group>
                    </b-col>
                    <b-col md="12">
                      <b-form-group label="Disposition" label-class="field-label-small" class="mb-0">
                        <b-form-input v-model="item.disposition" placeholder="e.g. Handed over to forest department" class="custom-input"></b-form-input>
                      </b-form-group>
                    </b-col>
                  </b-row>
                </div>
              </div>

              <div class="section-title mt-5 mb-4 d-flex align-items-center justify-content-between">
                <div class="d-flex align-items-center">
                  <div class="icon-box mr-3 bg-info"><i class="fa fa-cubes"></i></div>
                  <h5 class="mb-0 poppinsbold">Seizures: Wild Articles</h5>
                </div>
                <b-button variant="outline-info" size="sm" @click="addItem('wildlifeArticles')" class="rounded-pill px-3">
                  <i class="fa fa-plus mr-1"></i> Add Entry
                </b-button>
              </div>

              <div v-if="!operationData.seizures.wildlifeArticles || operationData.seizures.wildlifeArticles.length === 0" class="empty-state text-center p-4 border rounded mb-4">
                <p class="text-muted mb-0 small">No wildlife articles recorded.</p>
              </div>

              <div v-for="(item, index) in operationData.seizures.wildlifeArticles" :key="'art-' + index" class="entry-card mb-4 article-entry">
                <div class="entry-header d-flex justify-content-between align-items-center px-3 py-2 bg-info-light">
                  <span class="small font-weight-bold text-uppercase">Article #{{ index + 1 }}</span>
                  <b-button variant="link" class="text-danger p-0 small" @click="removeItem('wildlifeArticles', index)">
                    <i class="fa fa-trash mr-1"></i> Remove
                  </b-button>
                </div>
                <div class="p-3">
                  <b-row>
                    <b-col md="8">
                      <b-form-group label="Species Source" label-class="field-label-small">
                        <b-form-select v-model="item.species" required class="custom-select">
                          <option :value="null">Select Species</option>
                          <option v-for="s in speciesOptions" :key="s._id" :value="s._id">{{ s.commonName }}</option>
                        </b-form-select>
                      </b-form-group>
                    </b-col>
                    <b-col md="4">
                      <b-form-group label="Quantity/Count" label-class="field-label-small" class="mb-0">
                        <b-form-input type="number" v-model="item.count" min="1" class="custom-input"></b-form-input>
                      </b-form-group>
                    </b-col>
                  </b-row>
                </div>
              </div>
            </div>

            <!-- Accused Section -->
            <div class="form-section mb-5">
              <div class="section-title mb-4 d-flex align-items-center">
                <div class="icon-box mr-3 bg-danger"><i class="fa fa-users"></i></div>
                <h5 class="mb-0 poppinsbold">Accused Details</h5>
              </div>
              <b-form-group label="Link Accused from Database" label-class="field-label">
                <div class="accused-selection-box border rounded p-4 bg-white shadow-sm">
                  <div class="d-flex mb-4">
                    <div class="flex-grow-1 mr-2">
                        <va-select
                        v-model="selectedOffender"
                        :options="offenderOptions"
                        textBy="displayName"
                        keyBy="_id"
                        searchable
                        @update:search-value="handleSearch"
                        placeholder="Search for an offender by name or alias..."
                      />
                    </div>
                    <b-button variant="danger" @click="addAccused" :disabled="!selectedOffender" class="px-4 mr-2">
                      <i class="fa fa-plus mr-2"></i> Add Accused
                    </b-button>
                    <b-button variant="outline-danger" @click="showAddOffenderModal = true" class="px-4">
                      <i class="fa fa-user-plus mr-2"></i> Create New Offender
                    </b-button>
                  </div>

                  <div v-if="operationData.accusedDetails && operationData.accusedDetails.length" class="accused-list mt-3">
                    <div v-for="(item, index) in operationData.accusedDetails" :key="index" class="accused-item-row p-3 mb-2 rounded border d-flex align-items-center justify-content-between bg-light">
                      <div class="d-flex align-items-center">
                        <div class="avatar-circle mr-3 bg-secondary text-white shadow-sm">
                          <i class="fa fa-user"></i>
                        </div>
                        <div>
                          <p class="mb-0 font-weight-bold">{{ getOffenderName(item.offender) }}</p>
                          <div class="d-flex align-items-center mt-1">
                             <span class="small text-muted mr-2">Status:</span>
                             <b-form-select v-model="item.status" size="sm" :options="['arrested', 'absconding', 'detained']" class="status-select-mini"></b-form-select>
                          </div>
                        </div>
                      </div>
                      <b-button variant="link" class="text-danger p-0" @click="removeAccused(index)">
                        <i class="fa fa-times-circle fa-lg"></i>
                      </b-button>
                    </div>
                  </div>
                  <div v-else class="text-center py-5 border rounded bg-light border-dashed">
                    <i class="fa fa-users fa-3x text-light mb-3"></i>
                    <p class="text-muted mb-0">No accused linked to this operation yet.</p>
                  </div>
                </div>
              </b-form-group>
            </div>

            <!-- Legal & Media Section -->
            <div class="form-section mb-5" v-if="operationData.legalAction">
              <div class="section-title mb-4 d-flex align-items-center">
                <div class="icon-box mr-3 bg-warning"><i class="fa fa-legal"></i></div>
                <h5 class="mb-0 poppinsbold">Legal Action & Evidence</h5>
              </div>
              <b-row>
                <b-col md="4">
                  <b-form-group label="FIR / Offence No." label-class="field-label">
                    <b-form-input v-model="operationData.legalAction.firNo" required class="custom-input"></b-form-input>
                  </b-form-group>
                </b-col>
                <b-col md="4">
                  <b-form-group label="Applicable Sections" label-class="field-label">
                    <b-form-input v-model="operationData.legalAction.applicableSections" placeholder="e.g. Sec 9, 39, 44" class="custom-input"></b-form-input>
                  </b-form-group>
                </b-col>
                <b-col md="4">
                  <b-form-group label="Officer In-charge" label-class="field-label">
                    <b-form-input v-model="operationData.legalAction.officerInCharge" class="custom-input"></b-form-input>
                  </b-form-group>
                </b-col>
                <b-col md="12" class="mt-3">
                  <b-form-group label="Operation Media (Documents, Images or Videos)" label-class="field-label">
                    <div class="custom-file-upload border p-3 rounded bg-white text-center mb-3">
                      <input 
                        type="file" 
                        id="op-file-upload" 
                        ref="opFileInput"
                        multiple 
                        accept="image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.zip" 
                        @change="handleFileUpload" 
                        style="display: none;" 
                      />
                      <b-button variant="outline-primary" @click="$refs.opFileInput.click()" :disabled="isUploading">
                        <i class="fa fa-spinner fa-spin mr-2" v-if="isUploading"></i>
                        <i class="fa fa-upload mr-2" v-else></i>
                        {{ isUploading ? 'Uploading...' : 'Choose Files' }}
                      </b-button>
                      <div class="small text-muted mt-2">
                        Supported formats: Images, Videos, PDF, Word, Excel, ZIP (Max 150MB)
                      </div>
                    </div>

                    <!-- Uploaded Files List -->
                    <div v-if="operationData.photosVideos && operationData.photosVideos.length" class="mt-3">
                      <h6 class="font-weight-bold mb-2">Uploaded Files:</h6>
                      <b-row>
                        <b-col cols="12" md="6" v-for="(link, index) in operationData.photosVideos" :key="index" class="mb-2">
                          <div class="d-flex align-items-center p-2 border rounded bg-light">
                            <!-- Icon or Thumbnail -->
                            <div class="mr-2">
                              <img 
                                v-if="isImageFile(link)" 
                                :src="getFileUrl(link)" 
                                style="width: 40px; height: 40px; object-fit: cover;" 
                                class="rounded border" 
                              />
                              <i v-else-if="isPdfFile(link)" class="fa fa-file-pdf-o fa-2x text-danger px-2"></i>
                              <i v-else-if="isVideoFile(link)" class="fa fa-file-video-o fa-2x text-primary px-2"></i>
                              <i v-else class="fa fa-file-text-o fa-2x text-secondary px-2"></i>
                            </div>
                            
                            <!-- Details -->
                            <div class="flex-grow-1 overflow-hidden">
                              <div class="text-truncate font-weight-bold small" :title="getFileName(link)">{{ getFileName(link) }}</div>
                            </div>

                            <!-- Remove -->
                            <div>
                              <b-button size="sm" variant="link" class="text-danger p-1" @click="removeMedia(index)">
                                <i class="fa fa-trash mr-1"></i>
                              </b-button>
                            </div>
                          </div>
                        </b-col>
                      </b-row>
                    </div>
                  </b-form-group>
                </b-col>

                <b-col md="12" class="mt-3">
                  <b-form-group label="Operation Media Links (Optional)" label-class="field-label" description="Separate multiple external URLs with commas">
                    <b-form-textarea v-model="mediaLinks" rows="3" placeholder="Paste links to photos or videos from the operation..." class="custom-input"></b-form-textarea>
                  </b-form-group>
                </b-col>
              </b-row>
            </div>

            <div class="action-footer mt-5 text-right p-4 border-top bg-light">
              <b-button v-if="isEditing" variant="secondary" size="lg" class="poppinsbold px-5 mr-3" @click="isEditing = false">
                Cancel
              </b-button>
              
              <template v-if="!crimeData.isOperationSubmitted">
                <b-button @click="saveOperationDraft(isEditing)" variant="info" size="lg" class="poppinsbold px-5 mr-3 shadow" :disabled="isUploading">
                  Save Draft
                </b-button>
                <b-button @click="submitOperationForApproval(isEditing)" variant="success" size="lg" class="poppinsbold px-5 shadow" :disabled="isUploading">
                  Submit for Approval
                </b-button>
              </template>
              <template v-else>
                <b-button @click="saveOperationDraft(true)" variant="success" size="lg" class="poppinsbold px-5 shadow" :disabled="isUploading">
                  Save Changes
                </b-button>
              </template>
            </div>
          </b-form>
        </div>

        <div v-else-if="!isEditing && (crimeData.isOperationSubmitted || crimeData.isOperationApproved)">
          <!-- Data Display View (Admin/View Mode) -->
          <div class="view-container" v-if="crimeData.operationData">
            <div class="summary-banner p-4 rounded mb-5 shadow-sm d-flex flex-wrap justify-content-between">
              <div class="summary-item mb-3">
                <span class="label">STATUS</span>
                <div class="mt-1">
                  <va-badge v-if="crimeData.isOperationApproved" color="success" class="px-3">APPROVED</va-badge>
                  <va-badge v-else color="warning" class="px-3">PENDING APPROVAL</va-badge>
                </div>
              </div>
              <div class="summary-item mb-3">
                <span class="label">DATE OF OPERATION</span>
                <div class="value mt-1 poppinsbold">{{ formatDate(crimeData.operationData.dateTime) }}</div>
              </div>
              <div class="summary-item mb-3">
                <span class="label">LOCATION</span>
                <div class="value mt-1 poppinsbold">
                  {{ (crimeData.operationData.location && crimeData.operationData.location.exactLocation) ? crimeData.operationData.location.exactLocation : 'N/A' }}
                </div>
                <div class="small text-muted" v-if="crimeData.operationData.location">
                  {{ crimeData.operationData.location.forestRange }}, {{ crimeData.operationData.location.district }}, {{ crimeData.operationData.location.state }}, {{ crimeData.operationData.location.country }}
                </div>
              </div>
            </div>

            <div class="row mb-5 px-3" v-if="crimeData.operationData.agenciesInvolved || crimeData.operationData.modusOperandi">
               <b-col md="6" v-if="crimeData.operationData.agenciesInvolved" class="mb-3">
                  <div class="p-3 bg-white border rounded shadow-xs h-100">
                    <h6 class="small font-weight-bold text-muted mb-2 text-uppercase">Agencies Involved</h6>
                    <p class="mb-0">{{ formatAgencies(crimeData.operationData.agenciesInvolved) }}</p>
                  </div>
               </b-col>
               <b-col md="6" v-if="crimeData.operationData.modusOperandi" class="mb-3">
                  <div class="p-3 bg-white border rounded shadow-xs h-100">
                    <h6 class="small font-weight-bold text-muted mb-2 text-uppercase">Modus Operandi</h6>
                    <p class="mb-0">{{ crimeData.operationData.modusOperandi }}</p>
                  </div>
               </b-col>
            </div>

            <b-row>
              <b-col lg="8">
                <div class="view-section mb-5" v-if="crimeData.operationData.seizures">
                  <h6 class="poppinsbold border-bottom pb-2 mb-3"><i class="fa fa-list-alt mr-2 text-primary"></i>Seizure Logs</h6>

                  <div class="mb-4">
                    <p class="small font-weight-bold text-muted mb-2">LIVE WILDLIFE</p>
                    <div v-if="crimeData.operationData.seizures.wildlifeLive && crimeData.operationData.seizures.wildlifeLive.length" class="seizure-list">
                      <div v-for="(item, i) in crimeData.operationData.seizures.wildlifeLive" :key="i" class="seizure-item p-3 mb-2 rounded border-left border-success">
                        <div class="d-flex justify-content-between">
                          <div>
                            <span class="font-weight-bold d-block">{{ item.species ? (item.species.commonName || item.species) : 'Unknown Species' }}</span>
                            <span class="small italic text-muted" v-if="item.species && item.species.scientificName">{{ item.species.scientificName }}</span>
                          </div>
                          <div class="text-right">
                             <va-badge color="success" outline>{{ item.count }} Units</va-badge>
                             <div class="small mt-1" v-if="item.species && item.species.schedule">Schedule: {{ item.species.schedule }}</div>
                          </div>
                        </div>
                        <div class="mt-2 small text-muted border-top pt-2">
                          <strong>Status:</strong> {{ item.status }} | <strong>Disposition:</strong> {{ item.disposition || 'N/A' }}
                        </div>
                      </div>
                    </div>
                    <div v-else class="text-muted small italic">No live wildlife seizures recorded.</div>
                  </div>

                  <div class="mb-4">
                    <p class="small font-weight-bold text-muted mb-2">WILDLIFE ARTICLES</p>
                    <div v-if="crimeData.operationData.seizures.wildlifeArticles && crimeData.operationData.seizures.wildlifeArticles.length" class="seizure-list">
                      <div v-for="(item, i) in crimeData.operationData.seizures.wildlifeArticles" :key="i" class="seizure-item p-3 mb-2 rounded border-left border-info">
                        <div class="d-flex justify-content-between">
                          <div>
                            <span class="font-weight-bold d-block">{{ item.species ? (item.species.commonName || item.species) : 'Unknown Species' }}</span>
                            <span class="small italic text-muted" v-if="item.species && item.species.scientificName">{{ item.species.scientificName }}</span>
                          </div>
                          <div class="text-right">
                            <va-badge color="info" outline>{{ item.count }} Units</va-badge>
                            <div class="small mt-1" v-if="item.species && item.species.schedule">Schedule: {{ item.species.schedule }}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div v-else class="text-muted small italic">No wildlife articles recorded.</div>
                  </div>
                </div>

                <div class="view-section mb-5">
                  <h6 class="poppinsbold border-bottom pb-2 mb-3"><i class="fa fa-users mr-2 text-danger"></i>Accused Details</h6>
                  <div v-if="crimeData.operationData.accusedDetails && crimeData.operationData.accusedDetails.length" class="d-flex flex-wrap">
                    <div v-for="item in crimeData.operationData.accusedDetails" :key="item._id" class="accused-card p-3 mr-3 mb-3 rounded border bg-white shadow-sm" style="min-width: 200px;">
                       <div class="d-flex align-items-center mb-2">
                         <i class="fa fa-user-circle fa-lg mr-2 text-secondary"></i>
                         <span class="font-weight-bold">{{ getOffenderName(item.offender) }}</span>
                         <va-badge color="danger" size="small" class="ml-auto">{{ item.status }}</va-badge>
                       </div>

                       <div v-if="typeof item.offender === 'object'" class="mt-2 pt-2 border-top">
                          <div v-if="item.offender.age" class="small text-muted mb-1"><i class="fa fa-calendar-o mr-1"></i> Age: {{ item.offender.age }}</div>
                          <div v-if="item.offender.address" class="small text-muted mb-1"><i class="fa fa-map-marker mr-1"></i> {{ item.offender.address }}</div>
                          <div v-if="item.offender.idDetails" class="small text-muted"><i class="fa fa-id-card-o mr-1"></i> {{ item.offender.idDetails }}</div>
                       </div>
                    </div>
                  </div>
                  <div v-else class="text-muted small italic">No accused records linked to this operation.</div>
                </div>
              </b-col>

              <b-col lg="4" v-if="crimeData.operationData.legalAction">
                <div class="view-section mb-5 p-3 bg-light rounded shadow-sm border">
                  <h6 class="poppinsbold border-bottom pb-2 mb-3">Legal Action</h6>
                  <div class="legal-detail mb-2">
                    <span class="small text-muted d-block">FIR / OFFENCE NO.</span>
                    <span class="font-weight-bold">{{ crimeData.operationData.legalAction.firNo }}</span>
                  </div>
                  <div class="legal-detail mb-2">
                    <span class="small text-muted d-block">SECTIONS</span>
                    <span class="font-weight-bold">{{ crimeData.operationData.legalAction.applicableSections }}</span>
                  </div>
                  <div class="legal-detail mb-4">
                    <span class="small text-muted d-block">OFFICER IN-CHARGE</span>
                    <span class="font-weight-bold">{{ crimeData.operationData.legalAction.officerInCharge }}</span>
                  </div>

                  <div v-if="crimeData.operationData.photosVideos && crimeData.operationData.photosVideos.length">
                    <h6 class="poppinsbold border-bottom pb-2 mb-3 mt-4">Evidence Media</h6>
                    <div class="media-links">
                      <b-row>
                        <b-col cols="12" md="6" v-for="(link, i) in crimeData.operationData.photosVideos" :key="i" class="mb-2">
                          <div class="d-flex align-items-center p-2 border rounded bg-white">
                            <div class="mr-2">
                              <img 
                                v-if="isImageFile(link)" 
                                :src="getFileUrl(link)" 
                                style="width: 45px; height: 45px; object-fit: cover;" 
                                class="rounded border" 
                              />
                              <i v-else-if="isPdfFile(link)" class="fa fa-file-pdf-o fa-2x text-danger px-2"></i>
                              <i v-else-if="isVideoFile(link)" class="fa fa-file-video-o fa-2x text-primary px-2"></i>
                              <i v-else class="fa fa-file-text-o fa-2x text-secondary px-2"></i>
                            </div>
                            <div class="flex-grow-1 overflow-hidden">
                              <div class="text-truncate font-weight-bold small" :title="getFileName(link)">{{ getFileName(link) }}</div>
                              <a :href="getFileUrl(link)" target="_blank" class="text-info small text-decoration-none">
                                <i class="fa fa-external-link mr-1"></i>Open File
                              </a>
                            </div>
                          </div>
                        </b-col>
                      </b-row>
                    </div>
                  </div>
                </div>
              </b-col>
            </b-row>

            <div class="d-flex justify-content-between align-items-center mt-5 p-4 bg-white border rounded shadow-sm">
              <div>
                <b-button v-if="canEdit" variant="warning" @click="startEdit" class="px-5 py-2 poppinsbold text-white shadow">
                  <i class="fa fa-edit mr-2"></i> Edit Operation Report
                </b-button>
              </div>
              <div v-if="isAdmin && crimeData.isOperationSubmitted && !crimeData.isOperationApproved" class="text-right">
                <b-button variant="success" @click="approveOperation" class="px-5 py-2 poppinsbold shadow">
                  Approve Operation Report
                </b-button>
              </div>
            </div>
          </div>
        </div>

        <div v-else class="empty-operation text-center py-5">
           <div class="mb-4">
             <i class="fa fa-clipboard fa-4x text-light"></i>
           </div>
           <h4 class="text-muted poppinsbold">Operation Report Pending</h4>
           <p class="text-muted">Once the AIR is approved, the operation report can be initiated by the analyst.</p>
        </div>
      </div>
    </va-card>

    <!-- Add Offender Modal -->
    <AddOffenderModal
      v-model="showAddOffenderModal"
      @saved="handleOffenderSaved"
    />
  </div>
</template>

<script>
import axios from 'axios'
import AddOffenderModal from '../Offenders/AddOffenderModal'

export default {
  components: {
    AddOffenderModal,
  },
  props: ['crimeData', 'appUser'],
  data () {
    return {
      showAddOffenderModal: false,
      isEditing: false,
      operationData: {
        dateTime: '',
        location: {
          country: 'India',
          state: '',
          district: '',
          exactLocation: '',
          forestRange: '',
        },
        agenciesInvolved: '',
        modusOperandi: '',
        seizures: {
          wildlifeLive: [],
          wildlifeArticles: [],
        },
        accusedDetails: [],
        legalAction: {
          firNo: '',
          applicableSections: '',
          officerInCharge: '',
        },
        photosVideos: [],
      },
      selectedOffender: '',
      mediaLinks: '',
      speciesOptions: [],
      offenderOptions: [],
      searchTimeout: null,
      isUploading: false,
    }
  },
  watch: {
    crimeData: {
      handler (newVal) {
        if (newVal && newVal.operationData) {
          const opData = JSON.parse(JSON.stringify(newVal.operationData))
          
          // Ensure location exists
          if (!opData.location) {
            opData.location = {
              country: 'India',
              state: '',
              district: '',
              exactLocation: '',
              forestRange: '',
            }
          }
          // Ensure seizures exists
          if (!opData.seizures) {
            opData.seizures = {
              wildlifeLive: [],
              wildlifeArticles: [],
            }
          } else {
            if (!opData.seizures.wildlifeLive) opData.seizures.wildlifeLive = []
            if (!opData.seizures.wildlifeArticles) opData.seizures.wildlifeArticles = []
          }
          // Ensure legalAction exists
          if (!opData.legalAction) {
            opData.legalAction = {
              firNo: '',
              applicableSections: '',
              officerInCharge: '',
            }
          }
          // Ensure arrays exist
          if (!opData.accusedDetails) opData.accusedDetails = []
          if (!opData.photosVideos) opData.photosVideos = []

          // Format agenciesInvolved as a clean string for the form
          if (opData.agenciesInvolved) {
            if (Array.isArray(opData.agenciesInvolved)) {
              opData.agenciesInvolved = opData.agenciesInvolved.join(', ')
            } else {
              opData.agenciesInvolved = String(opData.agenciesInvolved).replace(/[\[\]]/g, '')
            }
          } else {
            opData.agenciesInvolved = ''
          }

          this.operationData = opData
          
          if (this.operationData.dateTime) {
            const dateObj = new Date(this.operationData.dateTime)
            const timezoneOffset = dateObj.getTimezoneOffset() * 60000
            const localISOTime = (new Date(dateObj - timezoneOffset)).toISOString().slice(0, 16)
            this.operationData.dateTime = localISOTime
          }
        }
      },
      immediate: true,
      deep: true,
    },
  },
  computed: {
    isAdmin () {
      return this.appUser && this.appUser.usertype == 0
    },
    canEdit () {
      if (!this.appUser) return false
      if (this.appUser.usertype == 0) return true
      if (this.appUser.usertype == 2 && this.crimeData && this.crimeData.assignedTo) {
        const isAssigned = this.crimeData.assignedTo.includes(this.appUser.id)
        return isAssigned && !this.crimeData.isOperationSubmitted && !this.crimeData.isOperationApproved
      }
      return false
    },
  },
  mounted () {
    this.getSpecies()
    this.getOffenders()
  },
  methods: {
    getSpecies () {
      axios({
        method: 'POST',
        url: '/digitalguide/list',
        headers: { token: localStorage.getItem('token') },
        data: {
          page: 1,
          limit: 10000,
          status: 'active'
        }
      }).then((res) => {
        if (res.data.status) this.speciesOptions = res.data.data
      })
    },
    getOffenders (search = '') {
      const params = search ? { search } : {}
      axios({
        method: 'GET',
        url: '/offenders/list',
        params,
        headers: { token: localStorage.getItem('token') },
      }).then((res) => {
        if (res.data.status && Array.isArray(res.data.data)) {
          this.offenderOptions = res.data.data.map(o => {
            const name = o.name || 'Unknown'
            const alias = o.alias ? ` (${o.alias})` : ''
            return {
              ...o,
              displayName: name + alias,
            }
          })
        }
      }).catch(err => {
        console.error('Error fetching offenders:', err)
      })
    },
    handleSearch (val) {
      if (this.searchTimeout) clearTimeout(this.searchTimeout)
      this.searchTimeout = setTimeout(() => {
        this.getOffenders(val)
      }, 500)
    },
    addItem (type) {
      if (type === 'wildlifeLive') {
        if (!this.operationData.seizures.wildlifeLive) this.operationData.seizures.wildlifeLive = []
        this.operationData.seizures.wildlifeLive.push({ species: null, count: 1, status: 'Alive', disposition: '' })
      } else {
        if (!this.operationData.seizures.wildlifeArticles) this.operationData.seizures.wildlifeArticles = []
        this.operationData.seizures.wildlifeArticles.push({ species: null, count: 1 })
      }
    },
    removeItem (type, index) {
      if (this.operationData.seizures[type]) {
        this.operationData.seizures[type].splice(index, 1)
      }
    },
    formatDate (date) {
      if (!date) return 'N/A'
      try {
        const options = { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' }
        return new Date(date).toLocaleDateString(undefined, options)
      } catch (e) {
        return 'N/A'
      }
    },
    startEdit () {
      this.isEditing = true
      this.operationData = JSON.parse(JSON.stringify(this.crimeData.operationData || {
        dateTime: '',
        location: {
          country: 'India',
          state: '',
          district: '',
          exactLocation: '',
          forestRange: '',
        },
        agenciesInvolved: '',
        modusOperandi: '',
        seizures: {
          wildlifeLive: [],
          wildlifeArticles: [],
        },
        accusedDetails: [],
        legalAction: {
          firNo: '',
          applicableSections: '',
          officerInCharge: '',
        },
        photosVideos: [],
      }))

      // Format agenciesInvolved as a clean string for the form
      if (this.operationData.agenciesInvolved) {
        if (Array.isArray(this.operationData.agenciesInvolved)) {
          this.operationData.agenciesInvolved = this.operationData.agenciesInvolved.join(', ')
        } else {
          this.operationData.agenciesInvolved = String(this.operationData.agenciesInvolved).replace(/[\[\]]/g, '')
        }
      } else {
        this.operationData.agenciesInvolved = ''
      }

      if (this.operationData.dateTime) {
        const dateObj = new Date(this.operationData.dateTime)
        const timezoneOffset = dateObj.getTimezoneOffset() * 60000
        const localISOTime = (new Date(dateObj - timezoneOffset)).toISOString().slice(0, 16)
        this.operationData.dateTime = localISOTime
      }
      this.mediaLinks = ''
    },
    saveOperationDraft (isEdit) {
      if (confirm('Are you sure you want to save this Operation Report as a draft?')) {
        this.executeOperationSubmit('/crimereport/operation/update', 'Operation Report draft saved successfully', isEdit)
      }
    },
    submitOperationForApproval (isEdit) {
      if (confirm('Are you sure you want to submit this Operation Report for approval? Once submitted, you will not be able to edit it.')) {
        this.executeOperationSubmit('/crimereport/operation/submit', 'Operation Report submitted for approval successfully', isEdit)
      }
    },
    executeOperationSubmit (endpoint, successMsg, isEdit) {
      let payloadData = JSON.parse(JSON.stringify(this.operationData))

      // Convert agenciesInvolved to array of strings
      if (payloadData.agenciesInvolved) {
        payloadData.agenciesInvolved = String(payloadData.agenciesInvolved)
          .split(',')
          .map(link => link.trim())
          .filter(link => link)
      } else {
        payloadData.agenciesInvolved = []
      }

      // Ensure offender in accusedDetails is sent as ObjectId string, not a populated object
      if (payloadData.accusedDetails && Array.isArray(payloadData.accusedDetails)) {
        payloadData.accusedDetails = payloadData.accusedDetails.map(item => {
          if (item.offender && typeof item.offender === 'object') {
            return {
              ...item,
              offender: item.offender._id
            }
          }
          return item
        })
      }

      // Ensure species in seizures are sent as ObjectId strings, not populated objects
      if (payloadData.seizures) {
        if (payloadData.seizures.wildlifeLive && Array.isArray(payloadData.seizures.wildlifeLive)) {
          payloadData.seizures.wildlifeLive = payloadData.seizures.wildlifeLive.map(item => {
            if (item.species && typeof item.species === 'object') {
              return {
                ...item,
                species: item.species._id
              }
            }
            return item
          })
        }
        if (payloadData.seizures.wildlifeArticles && Array.isArray(payloadData.seizures.wildlifeArticles)) {
          payloadData.seizures.wildlifeArticles = payloadData.seizures.wildlifeArticles.map(item => {
            if (item.species && typeof item.species === 'object') {
              return {
                ...item,
                species: item.species._id
              }
            }
            return item
          })
        }
      }

      if (this.mediaLinks) {
        const extraLinks = this.mediaLinks.split(',').map(link => link.trim()).filter(link => link)
        payloadData.photosVideos = [...(payloadData.photosVideos || []), ...extraLinks]
      }

      // Convert dateTime to UTC format reliably from IST
      if (payloadData.dateTime) {
        let dtStr = payloadData.dateTime
        if (typeof dtStr === 'string' && dtStr.length === 16 && !dtStr.includes('Z') && !dtStr.includes('+')) {
          dtStr = dtStr + '+05:30'
        }
        payloadData.dateTime = new Date(dtStr).toISOString()
      }

      axios({
        method: 'POST',
        url: endpoint,
        headers: { token: localStorage.getItem('token') },
        data: {
          id: this.crimeData._id,
          operationData: payloadData,
        },
      })
        .then((response) => {
          if (response.data.status) {
            this.$dlg.alert(successMsg, () => {
              this.isEditing = false
              this.$emit('refresh')
            }, { messageType: 'success' })
          } else {
            this.$dlg.alert(response.data.msg, () => {}, { messageType: 'error' })
          }
        })
        .catch((err) => {
          console.error(err)
          this.$dlg.alert('Failed to process Operation Report', () => {}, { messageType: 'error' })
        })
    },
    approveOperation () {
      if (confirm('Approve this operation report?')) {
        axios({
          method: 'POST',
          url: '/crimereport/operation/approve',
          headers: { token: localStorage.getItem('token') },
          data: { id: this.crimeData._id },
        })
          .then((response) => {
            if (response.data.status) {
              this.$dlg.alert(response.data.msg, () => {
                this.$emit('refresh')
              }, { messageType: 'success' })
            } else {
              this.$dlg.alert(response.data.msg, () => {}, { messageType: 'error' })
            }
          })
          .catch((err) => {
            console.error(err)
            this.$dlg.alert('Failed to approve report', () => {}, { messageType: 'error' })
          })
      }
    },
    downloadPDF () {
      axios({
        method: 'POST',
        url: '/crimereport/operation/export',
        headers: { token: localStorage.getItem('token') },
        data: { id: this.crimeData._id },
        responseType: 'blob',
      })
        .then((response) => {
          const fileURL = window.URL.createObjectURL(new Blob([response.data]))
          const link = document.createElement('a')
          link.href = fileURL
          link.download = `OperationReport_${this.crimeData.crimeReportID || 'Report'}.pdf`
          link.click()
        })
        .catch((err) => {
          console.error(err)
          this.$dlg.alert('Failed to download PDF', () => {}, { messageType: 'error' })
        })
    },
    addAccused () {
      if (!this.selectedOffender) return

      const offenderId = (this.selectedOffender && this.selectedOffender._id)
        ? this.selectedOffender._id
        : this.selectedOffender

      if (!offenderId) return

      // Check for duplicates
      const exists = this.operationData.accusedDetails.some(acc => {
        const id = (acc.offender && acc.offender._id) ? acc.offender._id : acc.offender
        return String(id) === String(offenderId)
      })

      if (exists) {
        this.$dlg.alert('This offender is already added to the list.')
        return
      }

      this.operationData.accusedDetails.push({
        offender: offenderId,
        status: 'arrested',
      })
      this.selectedOffender = ''
    },
    handleOffenderSaved (savedOffender) {
      this.offenderOptions.push(savedOffender)
      this.selectedOffender = savedOffender
      this.addAccused()
    },
    removeAccused (index) {
      this.operationData.accusedDetails.splice(index, 1)
    },
    getOffenderName (offender) {
      if (!offender) return 'Unknown'

      // If it's already an object (populated from backend)
      if (typeof offender === 'object') {
        if (offender.displayName) return offender.displayName
        const name = offender.name || 'Unknown'
        return offender.alias ? `${name} (${offender.alias})` : name
      }

      // If it's an ID, look it up in loaded options
      const found = this.offenderOptions.find(o => String(o._id) === String(offender))
      return found ? found.displayName : 'Unknown'
    },
    formatAgencies (agencies) {
      if (!agencies) return 'N/A'
      if (Array.isArray(agencies)) return agencies.join(', ')
      return String(agencies).replace(/[\[\]]/g, '')
    },
    getFileUrl (url) {
      if (!url) return ''
      if (url.startsWith('http://') || url.startsWith('https://')) {
        return url
      }
      const key = url.startsWith('/') ? url.substring(1) : url
      return (this.baseURL || 'http://localhost:4003/document/get/') + key
    },
    isImageFile (url) {
      if (!url) return false
      return /\.(jpg|jpeg|png|gif|webp|svg|bmp|tiff)(\?.*)?$/i.test(url)
    },
    isVideoFile (url) {
      if (!url) return false
      return /\.(mp4|mov|avi|mkv|3gp|flv|webm)(\?.*)?$/i.test(url)
    },
    isPdfFile (url) {
      if (!url) return false
      return /\.pdf(\?.*)?$/i.test(url)
    },
    getFileName (url) {
      if (!url) return ''
      const parts = url.split('/')
      return parts[parts.length - 1]
    },
    async handleFileUpload (event) {
      const files = event.target.files
      if (!files || files.length === 0) return

      this.isUploading = true
      try {
        for (let i = 0; i < files.length; i++) {
          const file = files[i]
          const formData = new FormData()
          formData.append('file', file)

          const response = await axios({
            method: 'POST',
            url: '/crimereport/media/upload',
            headers: {
              token: localStorage.getItem('token'),
              'Content-Type': 'multipart/form-data',
            },
            data: formData,
          })

          if (response.data.status) {
            if (!this.operationData.photosVideos) {
              this.$set(this.operationData, 'photosVideos', [])
            }
            this.operationData.photosVideos.push(response.data.key)
          } else {
            this.$dlg.alert(response.data.msg || 'Upload failed', () => {}, { messageType: 'error' })
          }
        }
      } catch (err) {
        console.error(err)
        this.$dlg.alert('Error uploading file(s)', () => {}, { messageType: 'error' })
      } finally {
        this.isUploading = false
        if (this.$refs.opFileInput) {
          this.$refs.opFileInput.value = ''
        }
      }
    },
    removeMedia (index) {
      this.operationData.photosVideos.splice(index, 1)
    },
  },
}
</script>

<style scoped>
.main-card {
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0,0,0,0.05);
}

.icon-box {
  width: 40px;
  height: 40px;
  background-color: #007bff;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
}

.field-label {
  font-weight: 600 !important;
  color: #444;
  margin-bottom: 8px;
  font-size: 14px;
}

.field-label-small {
  font-weight: 600 !important;
  color: #666;
  font-size: 12px;
  margin-bottom: 4px;
}

.custom-input, .custom-select {
  border-radius: 8px !important;
  border: 1px solid #dee2e6 !important;
  padding: 10px 12px !important;
  height: auto !important;
  transition: all 0.3s ease;
}

.custom-input:focus, .custom-select:focus {
  border-color: #007bff !important;
  box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1) !important;
}

.entry-card {
  background: #fdfdfd;
  border: 1px solid #eaeaea;
  border-radius: 10px;
  overflow: hidden;
  transition: transform 0.2s ease;
}

.entry-card:hover {
  border-color: #007bff;
}

.entry-header {
  background-color: #f8f9fa;
  border-bottom: 1px solid #eaeaea;
}

.bg-info-light {
  background-color: #e3f2fd;
}

.custom-multi-select {
  min-height: 200px;
  border: none;
  font-size: 14px;
}

.custom-multi-select:focus {
  outline: none;
  box-shadow: none;
}

.avatar-circle {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
}

.status-select-mini {
  height: 30px !important;
  padding: 0 20px 0 8px !important;
  font-size: 12px !important;
  width: auto !important;
  min-width: 120px !important;
  border-radius: 4px !important;
  border: 1px solid #ddd !important;
  line-height: 1.5 !important;
}

.border-dashed {
  border-style: dashed !important;
  border-width: 2px !important;
}

.accused-item-row {
  transition: all 0.2s ease;
}

.accused-item-row:hover {
  border-color: #dc3545 !important;
  background-color: #fff !important;
  box-shadow: 0 2px 8px rgba(0,0,0,0.05);
}

.summary-banner {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border: 1px solid #dee2e6;
}

.summary-item .label {
  font-size: 10px;
  font-weight: 800;
  color: #888;
  letter-spacing: 1px;
}

.seizure-item {
  background: #fff;
  border: 1px solid #eee;
  transition: all 0.2s;
}

.seizure-item:hover {
  transform: translateX(5px);
}

.accused-tag {
  font-size: 13px;
  color: #333;
}

.media-link-item {
  text-decoration: none;
  color: #333;
  transition: background 0.2s;
}

.media-link-item:hover {
  background-color: #f0f4f8;
}

.italic { font-style: italic; }
.shadow-xs { box-shadow: 0 1px 3px rgba(0,0,0,0.1); }
</style>
